import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Container, Grid, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip, IconButton } from "@mui/material";
import VisaImg from "../../assets/visa_document.png";
import { HeadingText } from "../../utils/CustomTagComponents";
import VideoPlayer from "../../components/common/VideoPlayer";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const Requirements = ({
  title,
  docs_list,
  mediaType = "image",
  mediaLink = "",
  bgColor = "#FFF4E6",
  defaultHeight = 410, // base height
  defaultImageHeight = 460,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // const descRef = useRef(null);
  const [imgHeight, setImgHeight] = useState("auto");

  // useEffect(() => {
  //   if (descRef.current) {
  //     const contentHeight = descRef.current.offsetHeight;
  //     setImgHeight(
  //       contentHeight > defaultImageHeight
  //         ? contentHeight
  //         : defaultImageHeight
  //     );
  //   }
  // }, [desc, defaultImageHeight]);

  const textStyle = {
    color: "#555",
    mb: 1.5,

    // Better spacing
    lineHeight: 1.7,
    // letterSpacing: "0.2px",

    textAlign: "justify",

    // Prevent overflow
    wordBreak: "break-word",
    overflowWrap: "break-word",
    whiteSpace: "normal",

    maxWidth: "100%",
  };

  const getResponsiveHtml = (html) => {
    if (!html) return "";

    let clean = html
      // .replace(/style="[^"]*"/gi, "") // ✅ REMOVE ALL inline styles
      .replace(
        /<(?!ol\b|ul\b)([a-z][a-z0-9]*)\b([^>]*)\sstyle="[^"]*"([^>]*)>/gi,
        "<$1$2$3>"
      )
      .replace(/&nbsp;/gi, "")
      .replace(/<br\s*\/?>/gi, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/\u00A0/gi, " ")
      .replace(/\[empty_line\]/gi, "<br />");

    if (!isMobile) return clean;

    return clean
      .replace(/font-weight:\s*900/gi, "font-weight: 700").replace(/&nbsp;/gi, " ").replace(/\u00A0/gi, " ").replace(/\[empty_line\]/gi, "<br />");
  };

  // ✅ CONTENT STYLING (KEY PART)
  const contentStyles = {
    fontSize: { xs: 12, sm: 13, md: 14 },
    lineHeight: 1.625,
    textAlign: "justify",

    "& p": {
      marginBlockStart: '0px !important',
      marginBottom: "16px",
    },

    "& p:last-of-type": {
      marginBottom: "0px",
    },

    // ✅ REMOVE DEFAULT BULLETS
    "& ul": {
      listStyle: "none",
      paddingLeft: "0px",
      marginBottom: "12px",
    },

    // ✅ CUSTOM LIST ITEM
    "& li": {
      position: "relative",
      paddingLeft: "28px",
      marginBottom: "4px",
      lineHeight: 1.7,
    },
    // ✅ FIRST ITEM
    "& li:first-of-type": {
      marginTop: "-10px",
    },

    // ✅ LAST ITEM
    "& li:last-of-type": {
      marginBottom: "15px",
    },

    // ✅ ADD ARROW ICON
    "& li::before": {
      content: '"\\f35a"',
      fontFamily: "FontAwesome",
      position: "absolute",
      left: '5px',
      top: "2px",
      fontSize: "14px",
      color: "#fd0000",
    },

    "& h1, & h2, & h3, & h4": {
      marginTop: "20px",
      marginBottom: "12px",
      fontWeight: 600,
    },

    "& strong": {
      fontWeight: 600,
    },
  };

  const documents = docs_list || [];

  const mid = Math.ceil(documents.length / 2); // first 15 items
  const leftColumn = documents.slice(0, mid);
  const rightColumn = documents.slice(mid);
  return (
    <Box sx={{ py: { xs: 2, sm: 2, md: 3 }, background: "#fff" }}>
      <Container sx={{
        px: { xs: 3, sm: 4, md: 6 },
        paddingLeft: { xs: "24px !important", md: "48px !important" },
        paddingRight: { xs: "24px !important", md: "48px !important" }
      }}>
        <Box
          sx={{
            display: "inline-block",
            position: "relative",
            mb: 3,

            "&::after": {
              content: '""',
              position: "absolute",
              left: 0,
              bottom: -8,
              width: "200px",
              height: "2px",
              backgroundColor: "#fd0000",
              opacity: "0.5"
            }
          }}
        >

          <HeadingText
            mb={1}
            sx={{
              fontSize: { xs: "22px", sm: "26px", md: "30px" },
              lineHeight: "1.3 !important",
              fontWeight: 700
            }}
            dangerouslySetInnerHTML={{ __html: getResponsiveHtml(title) }}
          />
        </Box>

        <Box
          sx={{
            mt: 2,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",     // mobile → 1 column
              md: "1fr 1fr", // desktop → 2 columns
            },
            columnGap: 2, // horizontal space (right side)
            rowGap: 1,    // vertical space (bottom)
            alignItems: "start",
          }}
        >
          <Box>
            {leftColumn.map((item, index) => (
              <Accordion
                key={index}
                disableGutters
                sx={{
                  borderRadius: "10px !important",
                  mb: 1.5,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  border: "1px solid #e5e7eb",
                  "&:before": { display: "none" },
                  transition: "0.2s",

                  "&:hover": {
                    boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: "50px !important",
                    px: 2,

                    "& .MuiAccordionSummary-content": {
                      margin: "8px 0",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: "14px", sm: "16px", md: "16px" },
                      fontWeight: 500,
                      color: "black",
                    }}
                  >
                    {index + 1}. {item.key}
                  </Typography>
                </AccordionSummary>

                {item.value && (
                  <AccordionDetails
                    sx={{
                      background: "#f9fafb",
                      borderTop: "1px solid #eee",
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: "#374151",
                        lineHeight: 1.7,
                        ...contentStyles,
                      }}
                      dangerouslySetInnerHTML={{ __html: item.value }}
                    />
                  </AccordionDetails>
                )}
              </Accordion>
            ))}
          </Box>

          <Box>
            {rightColumn.map((item, index) => (
              <Accordion
                key={index + mid}
                disableGutters
                sx={{
                  borderRadius: "10px !important",
                  mb: 1.5,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  border: "1px solid #e5e7eb",
                  "&:before": { display: "none" },
                  transition: "0.2s",

                  "&:hover": {
                    boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: "50px !important",
                    px: 2,

                    "& .MuiAccordionSummary-content": {
                      margin: "8px 0",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: "14px", sm: "16px", md: "16px" },
                      fontWeight: 500,
                      color: "black",
                    }}
                  >
                    {index + mid + 1}. {item.key}
                  </Typography>
                </AccordionSummary>

                {item.value && (
                  <AccordionDetails
                    sx={{
                      background: "#f9fafb",
                      borderTop: "1px solid #eee",
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: "#374151",
                        lineHeight: 1.7,
                        ...contentStyles,
                      }}
                      dangerouslySetInnerHTML={{ __html: item.value }}
                    />
                  </AccordionDetails>
                )}
              </Accordion>
            ))}
          </Box>
        </Box>

        {/* <Grid container spacing={4}>
            <Box
              sx={{
                flex: { xs: "1 1 100%", md: "0 0 50%" },
                position: "relative",
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0px 4px 25px rgba(0, 0, 0, 0.2)",
              }}
            >
              {mediaType === "video" ? (
                <VideoPlayer
                  src={mediaLink}
                  width="100%"
                  height="100%"
                  iconSize={{ xs: 40, sm: 50, md: 60 }}
                />
              ) : (
                <Box
                  component="img"
                  src={mediaLink}
                  alt="About Media"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: 4,
                    display: "block",
                  }}
                />
              )}
            </Box>
        </Grid> */}
      </Container>
    </Box>
  );
};

export default Requirements;
